import React from 'react'

// Only match figures that look like quoted statistics:
// - Must have explicit +/- sign at START (like +70%, -10%)
// - OR have comparison symbols at START (like ~2.5×, >50%)
// - OR be explicit comparisons (vs +50%)
// NOT standalone numbers like "Fortune 25", "top 100", "26-100", years like "2012"
// The pattern requires the sign/symbol to be at the START to avoid matching "26-100" as "-100"
const QUOTED_FIGURE_PATTERN = /(?:^|[\s(])([+][\d,.]+%?|~[\d,.]+[×x%]?|[><≈][\d,.]+%?)(?=[\s,.):]|$)/g

export function colorizeNumericContent(content: React.ReactNode): React.ReactNode {
  return React.Children.map(content, (child, index) => {
    if (typeof child === 'string') {
      return renderColoredString(child, `num-${index}`)
    }

    if (React.isValidElement(child)) {
      const childProps = child.props as { children?: React.ReactNode }
      return React.cloneElement(child as React.ReactElement<any>, {
        children: colorizeNumericContent(childProps?.children),
      })
    }

    return child
  })
}

function renderColoredString(text: string, keyPrefix: string): React.ReactNode {
  // More selective approach - only color explicit positive indicators (+X%, ~X, >X)
  // Do NOT color negative numbers to avoid false positives like "26-100"
  const positivePattern = /([+][\d,.]+%?|~[\d,.]+[×x%]?|[>][\d,.]+%?)/g
  
  const parts: React.ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  
  while ((match = positivePattern.exec(text)) !== null) {
    // Check if this is truly a standalone figure (not part of a larger pattern like "26-100")
    const prevChar = match.index > 0 ? text[match.index - 1] : ' '
    const isStandalone = /[\s(,:]/.test(prevChar) || match.index === 0
    
    if (!isStandalone) continue
    
    // Add text before match
    if (match.index > lastIndex) {
      parts.push(
        <React.Fragment key={`${keyPrefix}-text-${lastIndex}`}>
          {text.slice(lastIndex, match.index)}
        </React.Fragment>
      )
    }
    
    // Add colored match - only green for positive indicators
    const matchedText = match[0]
    parts.push(
      <span key={`${keyPrefix}-num-${match.index}`} className="text-emerald-600 dark:text-emerald-400">
        {matchedText}
      </span>
    )
    
    lastIndex = match.index + matchedText.length
  }
  
  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(
      <React.Fragment key={`${keyPrefix}-text-end`}>
        {text.slice(lastIndex)}
      </React.Fragment>
    )
  }
  
  return parts.length > 0 ? parts : text
}

export function parseNumericValue(raw: unknown): number | null {
  if (typeof raw === 'number' && !isNaN(raw)) {
    return raw
  }
  if (typeof raw === 'string') {
    const cleaned = raw.replace(/[^0-9.+-]/g, '')
    if (!cleaned) return null
    const parsed = parseFloat(cleaned)
    return isNaN(parsed) ? null : parsed
  }
  return null
}

export function getNumericToneClass(value: number | null): string {
  if (value === null) return ''
  if (value > 0) return 'text-emerald-600 dark:text-emerald-400'
  if (value < 0) return 'text-red-600 dark:text-red-400'
  return ''
}

